import Fuse from 'fuse.js'
import { ref } from 'vue'

export default function useSubKreweNameValidator() {
  const subkrewesStore = useSubKrewesStore()
  const currentSubkreweName = ref('')
  const nameValidated = ref(false)
  const similarSubkrewes = ref<SubKrewe[]>([])
  const showSimilarNameDialog = ref(false)

  // Normalize name for better comparison
  const normalizeName = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/^(the\s+)?(krewe\s+of\s+)/i, '') // Remove "the krewe of" or "krewe of" from beginning
      .replace(/\s+/g, ' ') // Normalize spaces
      .trim()
  }

  // Ensure the subkrewes list is loaded without re-fetching on every call.
  // Re-fetching rebuilds subkrewe objects with empty members/captains arrays
  // until the async resolvers catch up, which causes dependent UI
  // (e.g. the "Abandon SubKrewe" banner) to flicker during name validation.
  const ensureSubkrewesLoaded = async () => {
    // Captain/member views populate the store via realtime subscriptions
    // (which don't set `initialized`). If we already have subkrewes in
    // memory, skip the fetch — it would overwrite live data with
    // unresolved objects and cause downstream UI flicker.
    if (subkrewesStore.initialized || subkrewesStore.subkrewesArray.length > 0) {
      return
    }
    await subkrewesStore.fetchSubKrewes()
  }

  // Check for duplicate/similar subkrewe names
  const checkSubkreweName = async (name: string): Promise<{
    isValid: boolean
    exactMatch: SubKrewe | undefined
    similarMatches: SubKrewe[]
  }> => {
    if (!name || name.length < 3) {
      return { isValid: false, exactMatch: undefined, similarMatches: [] }
    }

    currentSubkreweName.value = name

    try {
      await ensureSubkrewesLoaded()
      // Use subkrewesArray instead of subkrewes - this is the key fix
      const allSubkrewes = subkrewesStore.subkrewesArray

      // First check for exact match (case insensitive)
      const exactMatch = allSubkrewes.find(sk =>
        normalizeName(sk.name) === normalizeName(name),
      )

      if (exactMatch) {
        return { isValid: false, exactMatch, similarMatches: [] }
      }

      // Use Fuse.js for finding similar names
      const fuseOptions = {
        includeScore: true,
        threshold: 0.3,
        keys: ['name'],
      }

      const fuse = new Fuse(
        allSubkrewes.map(sk => ({ ...sk, name: normalizeName(sk.name) })),
        fuseOptions,
      )

      const searchResults = fuse.search(normalizeName(name))

      // Filter results to only include those with high similarity
      const similarMatches = searchResults
        .filter(result => result.score && result.score < 0.2) // Lower score = more similar
        .map(result => result.item as SubKrewe)

      return {
        isValid: similarMatches.length === 0,
        exactMatch: undefined,
        similarMatches,
      }
    }
    catch (error) {
      console.error('Error checking subkrewe name:', error)
      return { isValid: false, exactMatch: undefined, similarMatches: [] }
    }
  }

  // Check just for exact name match
  const checkExactNameMatch = async (name: string): Promise<SubKrewe | undefined> => {
    if (!name || name.length < 3) {
      return undefined
    }

    currentSubkreweName.value = name

    try {
      await ensureSubkrewesLoaded()
      const allSubkrewes = subkrewesStore.subkrewesArray

      // Check for exact match (case insensitive)
      return allSubkrewes.find(sk =>
        normalizeName(sk.name) === normalizeName(name),
      )
    }
    catch (error) {
      console.error('Error checking subkrewe name:', error)
      return undefined
    }
  }

  // Make sure checkForSimilarNames always returns an array
  const checkForSimilarNames = async (name: string): Promise<SubKrewe[]> => {
    if (!name || name.length < 3) {
      return []
    }

    try {
      await ensureSubkrewesLoaded()
      const allSubkrewes = subkrewesStore.subkrewesArray || []

      // Use Fuse.js for finding similar names
      const fuseOptions = {
        includeScore: true,
        threshold: 0.3,
        keys: ['name'],
      }

      const fuse = new Fuse(
        allSubkrewes.map(sk => ({ ...sk, name: normalizeName(sk.name) })),
        fuseOptions,
      )

      const searchResults = fuse.search(normalizeName(name))
      // Filter results to only include those with high similarity
      // but exclude exact matches
      return searchResults
        .filter((result) => {
          const item = result.item as SubKrewe
          // Skip exact matches
          if (normalizeName(item.name) === normalizeName(name)) return false
          // Keep only items with high similarity
          return result.score && result.score < 0.7
        })
        .map(result => result.item as SubKrewe)
    }
    catch (error) {
      console.error('Error checking for similar subkrewe names:', error)
      return [] // Return empty array in case of error
    }
  }

  // Rest of the code remains unchanged
  const resetValidation = () => {
    nameValidated.value = false
    similarSubkrewes.value = []
    showSimilarNameDialog.value = false
  }

  const confirmName = () => {
    nameValidated.value = true
    showSimilarNameDialog.value = false
  }

  return {
    checkSubkreweName,
    checkExactNameMatch,
    checkForSimilarNames,
    confirmName,
    resetValidation,
    currentSubkreweName,
    nameValidated,
    similarSubkrewes,
    showSimilarNameDialog,
  }
}
